import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCodeBranch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import BootstrapVue from "bootstrap-vue";
import Vue, { computed, reactive, ref } from "vue";
import { getGalaxyInstance } from "@/app";
import { DatatypesMapperModel } from "@/components/Datatypes/model";
import { useNodePosition } from "@/components/Workflow/Editor/composables/useNodePosition";
import WorkflowIcons from "@/components/Workflow/icons";
import { useWorkflowStores } from "@/composables/workflowStores";
import LoadingSpan from "@/components/LoadingSpan.vue";
import DraggableWrapper from "@/components/Workflow/Editor/DraggablePan.vue";
import NodeInput from "@/components/Workflow/Editor/NodeInput.vue";
import NodeOutput from "@/components/Workflow/Editor/NodeOutput.vue";
import Recommendations from "@/components/Workflow/Editor/Recommendations.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'Node',
    props: {
        id: { type: Number, required: true },
        contentId: { type: String, default: null },
        name: { type: String, default: null },
        step: { type: Object, required: true },
        datatypesMapper: { type: DatatypesMapperModel, required: true },
        activeNodeId: {
            type: null,
            required: true,
            validator: (v) => typeof v === "number" || v === null,
        },
        rootOffset: { type: Object, required: true },
        scroll: { type: Object, required: true },
        scale: { type: Number, default: 1 },
        highlight: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
    },
    emits: [
        "onRemove",
        "onActivate",
        "onChange",
        "onCreate",
        "onUpdate",
        "onClone",
        "onUpdateStepPosition",
        "pan-by",
        "onDragConnector",
        "stopDragging",
    ],
    setup(__props, { emit }) {
        const props = __props;
        Vue.use(BootstrapVue);
        library.add(faCodeBranch);
        const popoverShow = ref(false);
        const popoverId = computed(() => `popover-${props.id}`);
        const scrolledTo = ref(false);
        function remove() {
            emit("onRemove", props.id);
        }
        const el = ref(null);
        const elHtml = computed(() => { var _a, _b; return (_b = (_a = el.value) === null || _a === void 0 ? void 0 : _a.$el) !== null && _b !== void 0 ? _b : null; });
        const postJobActions = computed(() => props.step.post_job_actions || {});
        const workflowOutputs = computed(() => props.step.workflow_outputs || []);
        const { connectionStore, stateStore, stepStore } = useWorkflowStores();
        const isLoading = computed(() => { var _a; return Boolean((_a = stateStore.getStepLoadingState(props.id)) === null || _a === void 0 ? void 0 : _a.loading); });
        useNodePosition(elHtml, props.id, stateStore, computed(() => props.scale));
        const title = computed(() => props.step.label || props.step.name);
        const idString = computed(() => `wf-node-step-${props.id}`);
        const showRule = computed(() => { var _a, _b; return ((_a = props.step.inputs) === null || _a === void 0 ? void 0 : _a.length) > 0 && ((_b = props.step.outputs) === null || _b === void 0 ? void 0 : _b.length) > 0; });
        const iconClass = computed(() => `icon fa fa-fw ${WorkflowIcons[props.step.type]}`);
        const canClone = computed(() => props.step.type !== "subworkflow"); // Why ?
        const isEnabled = getGalaxyInstance().config.enable_tool_recommendations; // getGalaxyInstance is not reactive
        const isActive = computed(() => props.id === props.activeNodeId);
        const classes = computed(() => {
            return {
                "node-on-scroll-to": scrolledTo.value,
                "node-highlight": props.highlight || isActive.value,
                "is-active": isActive.value,
            };
        });
        const style = computed(() => {
            return { top: props.step.position.top + "px", left: props.step.position.left + "px" };
        });
        const errors = computed(() => { var _a; return props.step.errors || ((_a = stateStore.getStepLoadingState(props.id)) === null || _a === void 0 ? void 0 : _a.error); });
        const inputs = computed(() => {
            const connections = connectionStore.getConnectionsForStep(props.id);
            const extraStepInputs = stepStore.getStepExtraInputs(props.id);
            const stepInputs = [...extraStepInputs, ...(props.step.inputs || [])];
            const unknownInputs = [];
            connections.forEach((connection) => {
                if (connection.input.stepId == props.id && !stepInputs.find((input) => input.name === connection.input.name)) {
                    unknownInputs.push(connection.input.name);
                }
            });
            const invalidInputNames = [...new Set(unknownInputs)];
            const invalidInputTerminalSource = invalidInputNames.map((name) => {
                return { name, optional: false, extensions: [], valid: false, input_type: "dataset" };
            });
            return [...stepInputs, ...invalidInputTerminalSource];
        });
        const invalidOutputs = computed(() => {
            const connections = connectionStore.getConnectionsForStep(props.id);
            const invalidConnections = connections.filter((connection) => connection.output.stepId == props.id &&
                !props.step.outputs.find((output) => output.name === connection.output.name));
            const invalidOutputNames = [...new Set(invalidConnections.map((connection) => connection.output.name))];
            return invalidOutputNames.map((name) => {
                return { name, optional: false, datatypes: [], valid: false };
            });
        });
        const outputs = computed(() => {
            return [...props.step.outputs, ...invalidOutputs.value];
        });
        function onDragConnector(dragPosition, terminal) {
            emit("onDragConnector", dragPosition, terminal);
        }
        function onMoveTo(position) {
            emit("onUpdateStepPosition", props.id, {
                top: position.y + props.scroll.y.value / props.scale,
                left: position.x + props.scroll.x.value / props.scale,
            });
        }
        function onPanBy(panBy) {
            emit("pan-by", panBy);
        }
        function onStopDragging() {
            emit("stopDragging");
        }
        function onChange() {
            emit("onChange");
        }
        function onCreate(contentId, name) {
            emit("onCreate", contentId, name);
            popoverShow.value = false;
        }
        function onClone() {
            emit("onClone", props.id);
        }
        function makeActive() {
            emit("onActivate", props.id);
        }
        return { __sfc: true, props, emit, popoverShow, popoverId, scrolledTo, remove, el, elHtml, postJobActions, workflowOutputs, connectionStore, stateStore, stepStore, isLoading, title, idString, showRule, iconClass, canClone, isEnabled, isActive, classes, style, errors, inputs, invalidOutputs, outputs, onDragConnector, onMoveTo, onPanBy, onStopDragging, onChange, onCreate, onClone, makeActive, FontAwesomeIcon, reactive, LoadingSpan, DraggableWrapper, NodeInput, NodeOutput, Recommendations };
    }
});
